import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../services/api';
import Loading from '../components/Loading';
import PlatformConfirmModal from '../components/PlatformConfirmModal';
import PrimaryNav from '../components/PrimaryNav';
import { getApiUrl } from '../config/runtimeConfig';

import { getAccessToken, clearAuthStorage } from '../services/api';
const SOCKET_URL = getApiUrl().replace(/\/api$/, '');

export default function FriendsPage() {
  const [activeTab, setActiveTab] = useState('friends'); // 'friends' | 'requests'
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [removeFriendshipId, setRemoveFriendshipId] = useState(null);
  const socketRef = useRef(null);
  const searchTimer = useRef(null);
  const navigate = useNavigate();

  const token = getAccessToken();

  // Fetch friends and requests
  const fetchData = useCallback(async () => {
    try {
      setError('');
      const [friendsRes, requestsRes] = await Promise.all([
        api.friends.getAll(),
        api.friends.getRequests(),
      ]);
      setFriends(friendsRes.data.friends || []);
      setRequests(requestsRes.data.requests || []);
    } catch (err) {
      console.error('Fetch friends error:', err);
      if (err.response?.status === 401) {
        clearAuthStorage();
        navigate('/login');
        return;
      }
      setError('Failed to load friends data');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();

    // Socket.IO for real-time online/offline status
    socketRef.current = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    socketRef.current.on('friend:online', ({ userId }) => {
      setFriends(prev =>
        prev.map(f => f.friendUserId === userId ? { ...f, isOnline: true } : f)
      );
    });

    socketRef.current.on('friend:offline', ({ userId }) => {
      setFriends(prev =>
        prev.map(f => f.friendUserId === userId ? { ...f, isOnline: false } : f)
      );
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [token, navigate, fetchData]);

  // Debounced search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await api.friends.search(searchQuery);
        setSearchResults(res.data.users || []);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery]);

  async function handleSendRequest(userId) {
    try {
      setActionMsg('');
      await api.friends.sendRequest(userId);
      setActionMsg('Friend request sent!');
      setSearchQuery('');
      setSearchResults([]);
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send request';
      setActionMsg(msg);
      setTimeout(() => setActionMsg(''), 4000);
    }
  }

  async function handleAccept(friendshipId) {
    try {
      await api.friends.acceptRequest(friendshipId);
      setActionMsg('Friend request accepted!');
      setTimeout(() => setActionMsg(''), 3000);
      fetchData();
    } catch (err) {
      setActionMsg('Failed to accept');
      setTimeout(() => setActionMsg(''), 3000);
    }
  }

  async function handleDecline(friendshipId) {
    try {
      await api.friends.declineRequest(friendshipId);
      setRequests(prev => prev.filter(r => r.friendshipId !== friendshipId));
    } catch (err) {
      setActionMsg('Failed to decline');
      setTimeout(() => setActionMsg(''), 3000);
    }
  }

  function handleRemove(friendshipId) {
    setRemoveFriendshipId(friendshipId);
  }

  async function confirmRemoveFriend() {
    const friendshipId = removeFriendshipId;
    setRemoveFriendshipId(null);
    if (!friendshipId) return;
    try {
      await api.friends.removeFriend(friendshipId);
      setFriends(prev => prev.filter(f => f.friendshipId !== friendshipId));
      setActionMsg('Friend removed');
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err) {
      setActionMsg('Failed to remove');
      setTimeout(() => setActionMsg(''), 3000);
    }
  }

  async function handleBlock(userId) {
    try {
      await api.friends.blockUser(userId);
      setSearchResults(prev => prev.filter(u => u.id !== userId));
      setActionMsg('User blocked');
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err) {
      setActionMsg('Failed to block');
      setTimeout(() => setActionMsg(''), 3000);
    }
  }

  if (!token) return null;

  const friendCount = friends.length;

  return (
    <>
      {removeFriendshipId && (
        <PlatformConfirmModal
          title="Remove Friend?"
          message="Are you sure you want to remove this friend?"
          cancelLabel="Keep Friend"
          confirmLabel="Remove Friend"
          onCancel={() => setRemoveFriendshipId(null)}
          onConfirm={confirmRemoveFriend}
        />
      )}
    <div style={{ padding: '16px', maxWidth: '480px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'transparent', border: 'none',
            color: '#00d4aa', fontSize: '20px',
            cursor: 'pointer', padding: '8px 12px 8px 0',
          }}
        >
          ← Back
        </button>
        <h1 style={{ fontSize: '1.3rem', margin: 0, color: '#fff', flex: 1 }}>Friends</h1>
        <span style={{
          color: '#888', fontSize: '13px',
          background: '#1a1a1a', padding: '4px 10px',
          borderRadius: '20px',
        }}>
          {friendCount}/1000
        </span>
      </div>

      {/* Action message */}
      {actionMsg && (
        <div style={{
          background: actionMsg.includes('!') ? 'rgba(0,212,170,0.1)' : 'rgba(255,77,77,0.1)',
          border: `1px solid ${actionMsg.includes('!') ? '#00d4aa' : '#ff4444'}`,
          color: actionMsg.includes('!') ? '#00d4aa' : '#ff4444',
          padding: '10px 14px', borderRadius: '10px',
          marginBottom: '12px', fontSize: '13px', textAlign: 'center',
        }}>
          {actionMsg}
        </div>
      )}

      {/* Search Bar */}
      <div style={{ marginBottom: '16px', position: 'relative' }}>
        <input
          type="text"
          placeholder="Search by username..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{
            width: '100%', padding: '12px 16px',
            borderRadius: '12px', border: '1px solid #2a2a2a',
            background: '#111111', color: '#fff',
            fontSize: '14px', outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        {searching && (
          <span style={{ position: 'absolute', right: '14px', top: '12px', color: '#888', fontSize: '13px' }}>
            ...
          </span>
        )}

        {/* Search Results Dropdown */}
        {searchResults.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            background: '#1a1a1a', border: '1px solid #2a2a2a',
            borderRadius: '0 0 12px 12px', zIndex: 10,
            maxHeight: '240px', overflowY: 'auto',
          }}>
            {searchResults.map(user => (
              <div
                key={user.id}
                style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', padding: '12px 16px',
                  borderBottom: '1px solid #222',
                }}
              >
                <span style={{ color: '#fff', fontSize: '14px' }}>{user.username}</span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={() => handleSendRequest(user.id)}
                    style={{
                      background: '#00d4aa', border: 'none',
                      color: '#000', padding: '6px 12px',
                      borderRadius: '8px', fontSize: '12px',
                      fontWeight: 'bold', cursor: 'pointer',
                    }}
                  >
                    Add
                  </button>
                  <button
                    onClick={() => handleBlock(user.id)}
                    style={{
                      background: 'transparent', border: '1px solid #ff4444',
                      color: '#ff4444', padding: '6px 10px',
                      borderRadius: '8px', fontSize: '11px',
                      cursor: 'pointer',
                    }}
                  >
                    Block
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', marginBottom: '16px',
        background: '#111111', borderRadius: '12px',
        padding: '4px',
      }}>
        <button
          onClick={() => setActiveTab('friends')}
          style={{
            flex: 1, padding: '10px',
            borderRadius: '10px', border: 'none',
            background: activeTab === 'friends' ? '#00d4aa' : 'transparent',
            color: activeTab === 'friends' ? '#000' : '#888',
            fontWeight: 'bold', fontSize: '14px',
            cursor: 'pointer', transition: 'all 0.2s',
          }}
        >
          Friends ({friends.length})
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          style={{
            flex: 1, padding: '10px',
            borderRadius: '10px', border: 'none',
            background: activeTab === 'requests' ? '#00d4aa' : 'transparent',
            color: activeTab === 'requests' ? '#000' : '#888',
            fontWeight: 'bold', fontSize: '14px',
            cursor: 'pointer', transition: 'all 0.2s',
          }}
        >
          Requests ({requests.length})
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <Loading variant="section" label="Loading friends…" />
      )}

      {/* Error */}
      {!loading && error && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <p style={{ color: '#ff4444', marginBottom: '12px' }}>{error}</p>
          <button
            onClick={fetchData}
            style={{
              background: '#00d4aa', border: 'none', color: '#000',
              padding: '10px 24px', borderRadius: '10px',
              fontWeight: 'bold', cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Friends Tab */}
      {!loading && activeTab === 'friends' && (
        <div>
          {friends.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '60px 0',
              color: '#666', fontSize: '14px',
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '12px' }}>👥</div>
              <p>No friends yet</p>
              <p style={{ fontSize: '12px' }}>Search by username above to add friends!</p>
            </div>
          ) : (
            friends.map(friend => (
              <div
                key={friend.friendshipId}
                style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', gap: '12px', padding: '14px 16px',
                  background: '#111111', borderRadius: '12px',
                  marginBottom: '8px', border: '1px solid #2a2a2a',
                  cursor: 'pointer',
                }}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/profile/${encodeURIComponent(friend.friendUserId)}`)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/profile/${encodeURIComponent(friend.friendUserId)}`); }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                  {/* Online/Offline Dot */}
                  <span style={{
                    width: '10px', height: '10px', borderRadius: '50%',
                    background: friend.isOnline ? '#00d4aa' : '#444',
                    display: 'inline-block', flexShrink: 0,
                    boxShadow: friend.isOnline ? '0 0 8px rgba(0,212,170,0.5)' : 'none',
                  }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: '#fff', fontWeight: '600', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {friend.username}
                    </div>
                    <div style={{ color: '#666', fontSize: '11px' }}>
                      {friend.isOnline ? 'Online' : 'Offline'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <button
                    type="button"
                    aria-label={`View ${friend.username}'s profile`}
                    onClick={e => { e.stopPropagation(); navigate(`/profile/${encodeURIComponent(friend.friendUserId)}`); }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      minHeight: '42px', padding: '8px 13px',
                      background: 'rgba(0,212,170,0.1)', border: '1px solid #00d4aa',
                      color: '#00d4aa', borderRadius: '9px', fontSize: '12px',
                      cursor: 'pointer', fontWeight: '700', whiteSpace: 'nowrap',
                    }}
                  >
                    View Profile <span aria-hidden="true" style={{ marginLeft: '5px', fontSize: '15px' }}>→</span>
                  </button>
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); handleRemove(friend.friendshipId); }}
                    style={{
                      minHeight: '42px', background: 'transparent', border: '1px solid #ff4444',
                      color: '#ff4444', padding: '8px 12px',
                      borderRadius: '9px', fontSize: '12px',
                      cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap',
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Requests Tab */}
      {!loading && activeTab === 'requests' && (
        <div>
          {requests.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '60px 0',
              color: '#666', fontSize: '14px',
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📩</div>
              <p>No pending requests</p>
            </div>
          ) : (
            requests.map(req => (
              <div
                key={req.friendshipId}
                style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', padding: '14px 16px',
                  background: '#111111', borderRadius: '12px',
                  marginBottom: '8px', border: '1px solid #2a2a2a',
                }}
              >
                <div>
                  <div style={{ color: '#fff', fontWeight: '600', fontSize: '14px' }}>
                    {req.username}
                  </div>
                  <div style={{ color: '#666', fontSize: '11px' }}>
                    {new Date(req.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleAccept(req.friendshipId)}
                    style={{
                      background: '#00d4aa', border: 'none',
                      color: '#000', padding: '8px 16px',
                      borderRadius: '8px', fontSize: '13px',
                      fontWeight: 'bold', cursor: 'pointer',
                    }}
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => handleDecline(req.friendshipId)}
                    style={{
                      background: 'transparent', border: '1px solid #ff4444',
                      color: '#ff4444', padding: '8px 14px',
                      borderRadius: '8px', fontSize: '12px',
                      cursor: 'pointer', fontWeight: '600',
                    }}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <style>{`
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #111; }
        ::-webkit-scrollbar-thumb { background: #333; border-radius: 4px; }
      `}</style>
      <PrimaryNav />
    </div>
    </>

  );
}
